import { ChevronUp, PlusCircle, RotateCcw, Info } from "feather-icons-react/build/IconComponents";
import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader } from "../../core/redux/action";
import { OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { message, Empty, Spin } from "antd";
import { fetchSalesUpdateSummary } from "../../core/api/data";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { uploadEmail } from "../../core/api/settings";
import OrdersWithoutMatchesModal from "./components/OrdersWithoutMatchesModal";
import {
  shouldFetchFreshData,
  setCachedData,
  clearExpiredCache,
} from "../../utils/salesUpdateCache";
import "./salesUpdate.css";

const SalesUpdate = ({ timezone, filters, refreshTrigger }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [salesUpdateData, setSalesUpdateData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState("0.00");
  const [totalNumber, setTotalNumber] = useState(0);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [emailAddress, setEmailAddress] = useState("");
  const [showOrdersWithoutMatchesModal, setShowOrdersWithoutMatchesModal] = useState(false);
  const [selectedTransactionType, setSelectedTransactionType] = useState(null);
  const [selectedMerchantName, setSelectedMerchantName] = useState(null);
  const [isCacheUsed, setIsCacheUsed] = useState(false);
  const isLoadingRef = useRef(false);
  const lastFiltersRef = useRef(null);

  // Clear expired cache on component mount
  useEffect(() => {
    clearExpiredCache();
  }, []);

  useEffect(() => {
    // Only fetch if we have valid filters and we're not already loading
    if (filters && filters.brands && filters.brands.length > 0 && !isLoadingRef.current) {
      // Check if filters have actually changed to prevent unnecessary calls
      const filtersString = JSON.stringify(filters);
      if (filtersString !== lastFiltersRef.current) {
        lastFiltersRef.current = filtersString;
        fetchSalesUpdateData(filters);
      }
    }
  }, [filters, refreshTrigger]);

  const openOrdersWithoutMatchesModal = (transactionType = null, merchantName = null) => {
    setSelectedTransactionType(transactionType);
    setSelectedMerchantName(merchantName);
    setShowOrdersWithoutMatchesModal(true);
  };

  const fetchSalesUpdateData = useCallback(
    async (currentFilters, forceRefresh = false) => {
      if (!currentFilters || !currentFilters.brands || currentFilters.brands.length === 0) {
        message.error("Please select a brand");
        return;
      }

      // Smart caching logic
      const cacheDecision = shouldFetchFreshData(currentFilters);

      // If we have cached data to use immediately, display it first
      if (cacheDecision.useCache && cacheDecision.cachedData && !forceRefresh) {
        setSalesUpdateData(cacheDecision.cachedData.processedData);
        setTotalAmount(cacheDecision.cachedData.totalAmount);
        setTotalNumber(cacheDecision.cachedData.totalNumber);
        setIsCacheUsed(true);

        // If we don't need to fetch fresh data, we're done
        if (!cacheDecision.shouldFetch) {
          return;
        }

        // Otherwise, we'll continue to fetch fresh data in the background
        console.log("Displaying cached data while fetching fresh data in background");
      }

      // Fetch fresh data if needed
      if (cacheDecision.shouldFetch || forceRefresh) {
        setLoading(true);
        isLoadingRef.current = true;

        try {
          console.log("Fetching fresh sales update data from backend");
          const response = await fetchSalesUpdateSummary(currentFilters);

          // Process the data for display
          const processedData = processDataForTable(
            response.sublyticsData,
            response.webOrdersData,
            response.tigerTechData,
            response.nexRepData
          );

          setSalesUpdateData(processedData);

          // Cache the processed data along with totals
          const dataToCache = {
            processedData,
            totalAmount,
            totalNumber,
          };
          setCachedData(currentFilters, dataToCache);
          setIsCacheUsed(false);
        } catch (error) {
          console.error("Error fetching sales update data:", error);
          message.error("Failed to fetch sales update data");

          // If we don't have cached data to fall back on, clear the state
          if (!cacheDecision.useCache) {
            setSalesUpdateData([]);
          }
        } finally {
          setLoading(false);
          isLoadingRef.current = false;
        }
      }
    },
    [totalAmount, totalNumber]
  );

  // Function to determine color class based on difference value
  const getDifferenceColorClass = (value) => {
    // Convert to absolute value for comparison
    const absValue = Math.abs(value);

    if (value === 0) {
      return "diff-zero"; // Green for exact match (zero difference)
    } else if (absValue < 10) {
      return "diff-small-red"; // Light red for small differences
    } else if (absValue < 50) {
      return "diff-medium-red"; // Medium red for medium differences
    } else if (absValue < 200) {
      return "diff-large-red"; // Strong red for large differences
    } else {
      return "diff-xlarge-red"; // Very intense red for very large differences
    }
  };

  // Helper function to create a complete data structure with all required fields
  const createCompleteDataStructure = (description) => {
    return {
      description,
      sublytics: { turnover: 0, number: 0 },
      wordpress: { turnover: 0, number: 0 },
      tigerTech: { turnover: 0, number: 0 },
      nexRep: { turnover: 0, number: 0 },
      reality: { turnover: 0, number: 0 },
      stripe: { turnover: 0, number: 0 },
      nmi: { turnover: 0, number: 0 },
      realityTotal: { turnover: 0, number: 0 },
    };
  };

  const processDataForTable = (sublyticsData, webOrdersData, tigerTechData, nexRepData) => {
    // Initialize variables for totals
    let totalSublyticsAmount = 0;
    let totalSublyticsNumber = 0;
    let totalWordPressAmount = 0;
    let totalWordPressNumber = 0;
    let totalTigerTechAmount = 0;
    let totalTigerTechNumber = 0;
    let totalNexRepAmount = 0;
    let totalNexRepNumber = 0;
    let totalTransactionsAmount = 0;
    let totalTransactionsNumber = 0;
    let totalStripeAmount = 0;
    let totalStripeNumber = 0;
    let totalNmiAmount = 0;
    let totalNmiNumber = 0;

    // Create a unified data structure to properly group by description
    const unifiedData = {};

    // Helper function to standardize gateway names for proper grouping
    const standardizeGatewayName = (name) => {
      // Standardize common variations like "GP - Auth.net #1" and "GP  - Auth.net #1"
      return name.trim().replace(/\s+/g, " ");
    };

    // Process Sublytics data
    if (sublyticsData) {
      Object.entries(sublyticsData).forEach(([merchant, info]) => {
        const standardName = standardizeGatewayName(merchant);
        if (!unifiedData[standardName]) {
          unifiedData[standardName] = createCompleteDataStructure(merchant); // Use helper function
        }
        unifiedData[standardName].sublytics.turnover += parseFloat(info.turnover);
        unifiedData[standardName].sublytics.number += parseInt(info.number, 10);

        // Process real Auth transactions from Sublytics
        if (info.real_auth_turnover) {
          unifiedData[standardName].reality.turnover += parseFloat(info.real_auth_turnover);
          unifiedData[standardName].reality.number += parseInt(info.real_auth_number, 10);
          totalTransactionsAmount += parseFloat(info.real_auth_turnover);
          totalTransactionsNumber += parseInt(info.real_auth_number, 10);
        }

        // Process real NMI transactions from Sublytics
        if (info.real_nmi_turnover) {
          unifiedData[standardName].nmi.turnover += parseFloat(info.real_nmi_turnover);
          unifiedData[standardName].nmi.number += parseInt(info.real_nmi_number, 10);
          totalNmiAmount += parseFloat(info.real_nmi_turnover);
          totalNmiNumber += parseInt(info.real_nmi_number, 10);
        }

        // Add to totals
        totalSublyticsAmount += parseFloat(info.turnover);
        totalSublyticsNumber += parseInt(info.number, 10);
      });
    }

    // Process TigerTech data
    if (tigerTechData) {
      Object.entries(tigerTechData).forEach(([merchant, info]) => {
        const standardName = standardizeGatewayName(merchant);
        if (!unifiedData[standardName]) {
          unifiedData[standardName] = {
            description: merchant,
            sublytics: { turnover: 0, number: 0 },
            wordpress: { turnover: 0, number: 0 },
            tigerTech: { turnover: 0, number: 0 },
            nexRep: { turnover: 0, number: 0 },
            reality: { turnover: 0, number: 0 },
          };
        }
        unifiedData[standardName].tigerTech.turnover += parseFloat(info.turnover);
        unifiedData[standardName].tigerTech.number += parseInt(info.number, 10);

        // Add to totals
        totalTigerTechAmount += parseFloat(info.turnover);
        totalTigerTechNumber += parseInt(info.number, 10);
      });
    }

    // Process NexRep data
    if (nexRepData) {
      Object.entries(nexRepData).forEach(([merchant, info]) => {
        const standardName = standardizeGatewayName(merchant);
        if (!unifiedData[standardName]) {
          unifiedData[standardName] = {
            description: merchant,
            sublytics: { turnover: 0, number: 0 },
            wordpress: { turnover: 0, number: 0 },
            tigerTech: { turnover: 0, number: 0 },
            nexRep: { turnover: 0, number: 0 },
            reality: { turnover: 0, number: 0 },
          };
        }
        unifiedData[standardName].nexRep.turnover += parseFloat(info.turnover);
        unifiedData[standardName].nexRep.number += parseInt(info.number, 10);

        // Add to totals
        totalNexRepAmount += parseFloat(info.turnover);
        totalNexRepNumber += parseInt(info.number, 10);
      });
    }

    // Ensure all entries have the necessary fields
    Object.keys(unifiedData).forEach((key) => {
      if (!unifiedData[key].reality) {
        unifiedData[key].reality = { turnover: 0, number: 0 };
      }
      if (!unifiedData[key].stripe) {
        unifiedData[key].stripe = { turnover: 0, number: 0 };
      }
      if (!unifiedData[key].nmi) {
        unifiedData[key].nmi = { turnover: 0, number: 0 };
      }
      if (!unifiedData[key].realityTotal) {
        unifiedData[key].realityTotal = { turnover: 0, number: 0 };
      }
    });

    // Process WordPress data
    if (webOrdersData) {
      Object.entries(webOrdersData).forEach(([merchant, info]) => {
        // Check if the merchant name contains 'authnet' (case insensitive)
        if (merchant.toLowerCase().includes("authnet")) {
          // Find an existing Auth.net entry to merge with
          let authNetKey = null;
          for (const key in unifiedData) {
            if (
              key.includes("Auth.net") ||
              key.includes("AUTH.NET") ||
              key.includes("auth.net") ||
              key.includes("Auth.net #")
            ) {
              authNetKey = key;
              break;
            }
          }

          if (authNetKey) {
            // Merge with existing Auth.net entry
            unifiedData[authNetKey].wordpress.turnover += parseFloat(info.turnover);
            unifiedData[authNetKey].wordpress.number += parseInt(info.number, 10);

            // Process real Auth transactions from WebOrders
            if (info.real_auth_turnover) {
              unifiedData[authNetKey].reality.turnover += parseFloat(info.real_auth_turnover);
              unifiedData[authNetKey].reality.number += parseInt(info.real_auth_number, 10);
              totalTransactionsAmount += parseFloat(info.real_auth_turnover);
              totalTransactionsNumber += parseInt(info.real_auth_number, 10);
            }
          } else {
            // No Auth.net entry found, create standardized entry
            const standardName = standardizeGatewayName(merchant);
            if (!unifiedData[standardName]) {
              unifiedData[standardName] = createCompleteDataStructure(merchant);
            }
            unifiedData[standardName].wordpress.turnover += parseFloat(info.turnover);
            unifiedData[standardName].wordpress.number += parseInt(info.number, 10);

            // Process real Auth transactions from WebOrders
            if (info.real_auth_turnover) {
              unifiedData[standardName].reality.turnover += parseFloat(info.real_auth_turnover);
              unifiedData[standardName].reality.number += parseInt(info.real_auth_number, 10);
              totalTransactionsAmount += parseFloat(info.real_auth_turnover);
              totalTransactionsNumber += parseInt(info.real_auth_number, 10);
            }
          }
        } else {
          // Normal processing for non-authnet entries
          const standardName = standardizeGatewayName(merchant);
          if (!unifiedData[standardName]) {
            unifiedData[standardName] = createCompleteDataStructure(merchant);
          }
          unifiedData[standardName].wordpress.turnover += parseFloat(info.turnover);
          unifiedData[standardName].wordpress.number += parseInt(info.number, 10);

          // Process real Stripe transactions from WebOrders for stripe_cc merchant
          if (merchant.toLowerCase().includes("stripe") && info.real_stripe_turnover) {
            unifiedData[standardName].stripe.turnover += parseFloat(info.real_stripe_turnover);
            unifiedData[standardName].stripe.number += parseInt(info.real_stripe_number, 10);
            totalStripeAmount += parseFloat(info.real_stripe_turnover);
            totalStripeNumber += parseInt(info.real_stripe_number, 10);
          }
        }

        // Add to totals
        totalWordPressAmount += parseFloat(info.turnover);
        totalWordPressNumber += parseInt(info.number, 10);
      });
    }

    // Calculate Reality Total (Authorize.Net + Stripe + NMI)
    Object.values(unifiedData).forEach((item) => {
      // Ensure reality property exists
      if (!item.reality) {
        item.reality = { turnover: 0, number: 0 };
      }

      // Ensure stripe property exists
      if (!item.stripe) {
        item.stripe = { turnover: 0, number: 0 };
      }

      // Ensure nmi property exists
      if (!item.nmi) {
        item.nmi = { turnover: 0, number: 0 };
      }

      // Ensure realityTotal property exists
      if (!item.realityTotal) {
        item.realityTotal = { turnover: 0, number: 0 };
      }

      // Safely get values with fallbacks
      const realityTurnover = typeof item.reality.turnover === "number" ? item.reality.turnover : 0;
      const realityNumber = typeof item.reality.number === "number" ? item.reality.number : 0;
      const stripeTurnover = typeof item.stripe.turnover === "number" ? item.stripe.turnover : 0;
      const stripeNumber = typeof item.stripe.number === "number" ? item.stripe.number : 0;
      const nmiTurnover = typeof item.nmi.turnover === "number" ? item.nmi.turnover : 0;
      const nmiNumber = typeof item.nmi.number === "number" ? item.nmi.number : 0;

      item.realityTotal.turnover = realityTurnover + stripeTurnover + nmiTurnover;
      item.realityTotal.number = realityNumber + stripeNumber + nmiNumber;
    });

    // Format values for display
    const formatAmount = (amount) =>
      `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Convert to array and sort
    const result = [];
    const sortedKeys = Object.keys(unifiedData).sort();

    // Process each merchant
    sortedKeys.forEach((key) => {
      const data = unifiedData[key];

      // Calculate combined values
      const crmTotalAmount = data.sublytics.turnover + data.wordpress.turnover;
      const crmTotalNumber = data.sublytics.number + data.wordpress.number;
      const callCenterTotalAmount = data.tigerTech.turnover + data.nexRep.turnover;
      const callCenterTotalNumber = data.tigerTech.number + data.nexRep.number;

      // Calculate differences
      const differenceAmount = data.sublytics.turnover - callCenterTotalAmount;
      const differenceNumber = data.sublytics.number - callCenterTotalNumber;

      // Add amount row
      result.push({
        type: "amount",
        description: data.description,
        sublyticsValue: formatAmount(data.sublytics.turnover),
        wordPressValue: formatAmount(data.wordpress.turnover),
        crmTotalValue: formatAmount(crmTotalAmount),
        tigerTechValue: formatAmount(data.tigerTech.turnover),
        nexRepValue: formatAmount(data.nexRep.turnover),
        callCenterTotalValue: formatAmount(callCenterTotalAmount),
        realityValue: formatAmount(data.reality.turnover),
        stripeValue: formatAmount(data.stripe.turnover),
        nmiValue: formatAmount(data.nmi.turnover),
        realityTotalValue: formatAmount(data.realityTotal.turnover),
        differenceValue: formatAmount(differenceAmount),
        showDetailsButton: data.sublytics.turnover > 0 && Math.abs(differenceAmount) > 0,
      });

      // Add number row
      result.push({
        type: "number",
        description: data.description,
        sublyticsValue: data.sublytics.number,
        wordPressValue: data.wordpress.number,
        crmTotalValue: crmTotalNumber,
        tigerTechValue: data.tigerTech.number,
        nexRepValue: data.nexRep.number,
        callCenterTotalValue: callCenterTotalNumber,
        realityValue: data.reality.number,
        stripeValue: data.stripe.number,
        nmiValue: data.nmi.number,
        realityTotalValue: data.realityTotal.number,
        differenceValue: differenceNumber,
        showDetailsButton: data.sublytics.number > 0 && Math.abs(differenceNumber) > 0,
      });
    });

    // Add total row for amounts
    result.push({
      type: "amount",
      description: "Total",
      sublyticsValue: `$${totalSublyticsAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      wordPressValue: `$${totalWordPressAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      crmTotalValue: `$${(totalSublyticsAmount + totalWordPressAmount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      tigerTechValue: `$${totalTigerTechAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      nexRepValue: `$${totalNexRepAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      callCenterTotalValue: `$${(totalTigerTechAmount + totalNexRepAmount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      realityValue: `$${totalTransactionsAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      stripeValue: `$${totalStripeAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      nmiValue: `$${totalNmiAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      realityTotalValue: `$${(
        totalTransactionsAmount +
        totalStripeAmount +
        totalNmiAmount
      ).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      differenceValue: `$${(
        totalSublyticsAmount -
        (totalTigerTechAmount + totalNexRepAmount)
      ).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    });

    // Add total row for numbers
    result.push({
      type: "number",
      description: "Total",
      sublyticsValue: totalSublyticsNumber,
      wordPressValue: totalWordPressNumber,
      crmTotalValue: totalSublyticsNumber + totalWordPressNumber,
      tigerTechValue: totalTigerTechNumber,
      nexRepValue: totalNexRepNumber,
      callCenterTotalValue: totalTigerTechNumber + totalNexRepNumber,
      realityValue: totalTransactionsNumber,
      stripeValue: totalStripeNumber,
      nmiValue: totalNmiNumber,
      realityTotalValue: totalTransactionsNumber + totalStripeNumber + totalNmiNumber,
      differenceValue: totalSublyticsNumber - (totalTigerTechNumber + totalNexRepNumber),
    });

    // Update state with totals
    setTotalAmount(
      (totalSublyticsAmount + totalWordPressAmount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
    setTotalNumber(totalSublyticsNumber + totalWordPressNumber);

    return result;
  };

  const handleExportClick = (type) => {
    setExportType(type);
    setIsExportModalVisible(true);
  };

  const exportToPDF = async (returnFile = false) => {
    const doc = new jsPDF("landscape");

    // Add title
    doc.setFontSize(16);
    doc.text("Sales Update Summary", 14, 15);

    // Calculate date range and number of days
    let dateRange = "All dates";
    let numDays = 0;

    if (filters.dateRange && filters.dateRange.length === 2) {
      const startDate = new Date(filters.dateRange[0]);
      const endDate = new Date(filters.dateRange[1]);
      dateRange = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;

      // Calculate number of days (inclusive)
      const diffTime = Math.abs(endDate - startDate);
      numDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }

    // Add date range info
    doc.setFontSize(10);
    doc.text(`Date Range: ${dateRange} (${numDays} days)`, 14, 25);

    // Define headers
    const headers = [
      "Description",
      "Sublytics",
      "WordPress",
      "TOTAL CRM Sublytics + WordPress",
      "TigerTech",
      "NEXREP",
      "Total (TigerTech + NEXREP)",
      "Difference (CRM Sublytics - (TigerTech + NEXREP))",
      "Reality Authorize.Net",
      "Reality Stripe",
      "Reality NMI",
      "Reality Total",
    ];

    // Prepare data for table
    const tableRows = [];

    salesUpdateData.forEach((row) => {
      const isNumberRow = row.type === "number";

      tableRows.push([
        isNumberRow ? "Nb of orders" : row.description,
        row.sublyticsValue,
        row.wordPressValue,
        row.crmTotalValue,
        row.tigerTechValue,
        row.nexRepValue,
        row.callCenterTotalValue,
        row.differenceValue,
        row.realityValue,
        row.stripeValue,
        row.nmiValue,
        row.realityTotalValue,
      ]);
    });

    autoTable(doc, {
      head: [headers],
      body: tableRows,
      startY: 35,
      theme: "striped",
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: 30 }, // Description column - keep wide
        1: { cellWidth: 20 }, // Sublytics
        2: { cellWidth: 20 }, // WordPress
        3: { cellWidth: 25 }, // TOTAL CRM Sublytics + WordPress
        4: { cellWidth: 20 }, // TigerTech
        5: { cellWidth: 17 }, // NEXREP
        6: { cellWidth: 25 }, // Total (TigerTech + NEXREP)
        7: { cellWidth: 30 }, // Difference
        8: { cellWidth: 20 }, // Reality Authorize.Net
        9: { cellWidth: 20 }, // Reality Stripe
        10: { cellWidth: 17 }, // Reality NMI
        11: { cellWidth: 17 }, // Reality Total
      },
      didParseCell: function (data) {
        // Style for number rows
        if (data.row.raw[0] === "Nb of orders") {
          data.cell.styles.fontStyle = "italic";
          data.cell.styles.fontSize = 7;
        }

        // Style for total rows
        if (
          data.row.raw[0] === "Total" ||
          (data.row.raw[0] !== "Nb of orders" && data.row.index % 2 === 0)
        ) {
          data.cell.styles.fontStyle = "bold";
        }

        // Style for difference column (index 7)
        if (
          data.column.index === 7 &&
          data.row.raw[7] !== "Difference (CRM Sublytics - (TigerTech + NEXREP))"
        ) {
          let value;
          const cellValue = data.row.raw[7];

          // Check if the value is a string (with $ sign) or a number
          if (typeof cellValue === "string" && cellValue.includes("$")) {
            value = parseFloat(cellValue.replace("$", ""));
          } else {
            value = parseFloat(cellValue);
          }

          if (!isNaN(value)) {
            if (value === 0) {
              data.cell.styles.fillColor = [163, 247, 163]; // Light green
            } else if (Math.abs(value) < 5) {
              data.cell.styles.fillColor = [204, 255, 204]; // Very light green
            } else if (Math.abs(value) < 20) {
              data.cell.styles.fillColor = [255, 255, 153]; // Light yellow
            } else if (Math.abs(value) < 50) {
              data.cell.styles.fillColor = [255, 204, 153]; // Light orange
            } else {
              data.cell.styles.fillColor = [255, 153, 153]; // Light red
            }
          }
        }

        // Style for Reality Authorize.Net column (index 8)
        if (data.column.index === 8 && data.row.raw[0] === "Total") {
          // Make the total for Reality Authorize.Net bold
          data.cell.styles.fontStyle = "bold";
        }

        // Style for Reality Stripe column (index 9)
        if (data.column.index === 9 && data.row.raw[0] === "Total") {
          // Make the total for Reality Stripe bold
          data.cell.styles.fontStyle = "bold";
        }

        // Style for Reality NMI column (index 10)
        if (data.column.index === 10 && data.row.raw[0] === "Total") {
          // Make the total for Reality NMI bold
          data.cell.styles.fontStyle = "bold";
        }

        // Style for Reality Total column (index 11)
        if (data.column.index === 11 && data.row.raw[0] === "Total") {
          // Make the total for Reality Total bold
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    if (returnFile) {
      const pdfBlob = doc.output("blob");
      return new File([pdfBlob], "Sales_Update_Summary.pdf", { type: "application/pdf" });
    } else {
      doc.save("Sales_Update_Summary.pdf");
    }
  };

  const exportToExcel = async (returnFile = false) => {
    const workbook = XLSX.utils.book_new();

    // Calculate date range and number of days
    let dateRange = "All dates";
    let numDays = 0;

    if (filters.dateRange && filters.dateRange.length === 2) {
      const startDate = new Date(filters.dateRange[0]);
      const endDate = new Date(filters.dateRange[1]);
      dateRange = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;

      // Calculate number of days (inclusive)
      const diffTime = Math.abs(endDate - startDate);
      numDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }

    // Prepare data for Excel with header information
    const excelData = [
      [`Sales Update Summary - Date Range: ${dateRange} (${numDays} days)`],
      [], // Empty row for spacing
      [
        "Description",
        "Sublytics",
        "WordPress",
        "TOTAL CRM Sublytics + WordPress",
        "TigerTech",
        "NEXREP",
        "Total (TigerTech + NEXREP)",
        "Difference (CRM Sublytics - (TigerTech + NEXREP))",
        "Reality Authorize.Net",
        "Reality Stripe",
        "Reality NMI",
        "Reality Total",
      ],
    ];

    salesUpdateData.forEach((row) => {
      const rowData = [
        row.type === "number" ? "Nb of orders" : row.description,
        row.sublyticsValue,
        row.wordPressValue,
        row.crmTotalValue,
        row.tigerTechValue,
        row.nexRepValue,
        row.callCenterTotalValue,
        row.differenceValue,
        row.realityValue,
        row.stripeValue,
        row.nmiValue,
        row.realityTotalValue,
      ];
      excelData.push(rowData);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths (similar to PDF export)
    const columnWidths = [
      { wch: 40 }, // Description column - keep wide
      { wch: 15 }, // Sublytics
      { wch: 15 }, // WordPress
      { wch: 25 }, // TOTAL CRM Sublytics + WordPress
      { wch: 15 }, // TigerTech
      { wch: 15 }, // NEXREP
      { wch: 20 }, // Total (TigerTech + NEXREP)
      { wch: 20 }, // Difference
      { wch: 20 }, // Reality Authorize.Net
      { wch: 20 }, // Reality Stripe
      { wch: 20 }, // Reality NMI
      { wch: 20 }, // Reality Total
    ];

    worksheet["!cols"] = columnWidths;

    // Apply cell styling
    // Make headers bold
    const headerRange = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
      const headerCell = XLSX.utils.encode_cell({ r: 2, c: C }); // Row 3 (0-indexed) contains headers
      if (!worksheet[headerCell]) continue;
      worksheet[headerCell].s = { font: { bold: true } };
    }

    // Style for total rows and number rows
    for (let R = 3; R < excelData.length; ++R) {
      // Start from row 4 (data rows)
      const firstCell = XLSX.utils.encode_cell({ r: R, c: 0 });
      if (!worksheet[firstCell]) continue;

      // Check if this is a total row or number row
      if (worksheet[firstCell].v === "Total" || worksheet[firstCell].v === "Nb of orders") {
        // Apply styling to all cells in this row
        for (let C = 0; C <= headerRange.e.c; ++C) {
          const cell = XLSX.utils.encode_cell({ r: R, c: C });
          if (!worksheet[cell]) continue;

          if (worksheet[firstCell].v === "Total") {
            worksheet[cell].s = { font: { bold: true } };
          } else if (worksheet[firstCell].v === "Nb of orders") {
            worksheet[cell].s = { font: { italic: true } };
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Update");

    if (returnFile) {
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const excelBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      return new File([excelBlob], "Sales_Update_Summary.xlsx", {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    } else {
      XLSX.writeFile(workbook, "Sales_Update_Summary.xlsx");
    }
  };

  const handleEmailSend = async () => {
    if (!emailAddress || !emailAddress.includes("@")) {
      message.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      let reportFile;
      try {
        if (exportType === "pdf") {
          reportFile = await exportToPDF(true);
        } else if (exportType === "xls") {
          reportFile = await exportToExcel(true);
        }
      } catch (genError) {
        console.error("Error in file generation:", genError);
        throw genError;
      }
      const formData = new FormData();
      if (exportType === "pdf") {
        formData.append("attachment", reportFile, "Sales_Update_Summary.pdf");
      } else if (exportType === "xls") {
        formData.append("attachment", reportFile, "Sales_Update_Summary.xlsx");
      }
      formData.append("to", emailAddress);
      formData.append("subject", "Exported Sales Update Summary");
      formData.append(
        "body",
        `Hello,\n\nPlease find the exported ${exportType.toUpperCase()} file attached.\n\nBest regards.`
      );

      const response = await uploadEmail(formData);

      const fileUrl = response.data.report_email.signed_attachment_url;
      const subject = encodeURIComponent("Exported Sales Update Summary");
      const body = encodeURIComponent(
        `Hello,\n\nPlease find the exported report at the following link:\n\n${fileUrl}\n\nBest regards.`
      );

      window.location.href = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
    } catch (error) {
      console.error("Error sending email:", error);
      message.error(`Failed to send email: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (exportType === "pdf") {
      exportToPDF();
    } else if (exportType === "xls") {
      exportToExcel();
    }
    setIsExportModalVisible(false);
  };

  return (
    <div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-header">
              <div className="add-item d-flex">
                <div className="page-title">
                  <h4>Sales Update</h4>
                  {isCacheUsed && (
                    <small className="text-muted ms-2">
                      <i className="fas fa-database"></i> Cached data
                    </small>
                  )}
                </div>
              </div>
              <ul className="table-top-head">
                <li>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Print</Tooltip>}>
                    <Link onClick={() => handleExportClick("pdf")}>
                      <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />{" "}
                    </Link>
                  </OverlayTrigger>
                </li>
                <li>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Excel</Tooltip>}>
                    <Link onClick={() => handleExportClick("xls")}>
                      <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />{" "}
                    </Link>
                  </OverlayTrigger>
                </li>
                <li>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
                    <Link
                      onClick={() => {
                        console.log("Manual refresh requested");
                        fetchSalesUpdateData(filters, true); // Force refresh
                      }}>
                      <RotateCcw />
                    </Link>
                  </OverlayTrigger>
                </li>
                <li>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Collapse</Tooltip>}>
                    <Link
                      id="collapse-header"
                      className={data ? "active" : ""}
                      onClick={() => dispatch(setToogleHeader(!data))}>
                      <ChevronUp />
                    </Link>
                  </OverlayTrigger>
                </li>
              </ul>
            </div>

            <div className="">
              <div className="">
                <div className="table-responsive">
                  <table className="table table-bordered text-center small-font table-striped rounded-sm sales-update-table">
                    <thead className="thead-dark">
                      <tr>
                        <th>Description</th>
                        <th>CRM Sublytics</th>
                        <th>WordPress</th>
                        <th>TOTAL CRM Sublytics + WordPress</th>
                        <th>TigerTech</th>
                        <th>NEXREP</th>
                        <th>Total (TigerTech + NEXREP)</th>
                        <th>
                          <div className="d-flex flex-column align-items-center">
                            <span>Difference (CRM Sublytics - (TigerTech + NEXREP))</span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="mt-1"
                              onClick={() => openOrdersWithoutMatchesModal()}
                              title="View orders without matches">
                              <Info size={14} /> See Details
                            </Button>
                          </div>
                        </th>
                        <th>Reality Authorize.Net</th>
                        <th>Reality Stripe</th>
                        <th>Reality NMI</th>
                        <th>Reality Total</th>
                        <th>CRM Sublytics + WordPress - Reality Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesUpdateData.length > 0 && !loading ? (
                        salesUpdateData.map((row, index) => {
                          const isAmountRow = row.type === "amount";
                          const isNumberRow = row.type === "number";
                          const isTotal = row.description === "Total";

                          return (
                            <tr
                              key={index}
                              className={`${
                                isTotal || (!isNumberRow && !isTotal) ? "font-weight-bold" : ""
                              } ${isNumberRow ? "number-row" : ""}`}>
                              <td>
                                {isNumberRow ? (
                                  <span className="number-label">Nb of orders</span>
                                ) : (
                                  row.description
                                )}
                              </td>
                              <td>
                                {isAmountRow
                                  ? row.sublyticsValue
                                  : isNumberRow
                                  ? row.sublyticsValue
                                  : ""}
                              </td>
                              <td>
                                {isAmountRow
                                  ? row.wordPressValue
                                  : isNumberRow
                                  ? row.wordPressValue
                                  : ""}
                              </td>
                              <td>
                                {isAmountRow
                                  ? row.crmTotalValue
                                  : isNumberRow
                                  ? row.crmTotalValue
                                  : ""}
                              </td>
                              <td>
                                {isAmountRow
                                  ? row.tigerTechValue
                                  : isNumberRow
                                  ? row.tigerTechValue
                                  : ""}
                              </td>
                              <td>
                                {isAmountRow ? row.nexRepValue : isNumberRow ? row.nexRepValue : ""}
                              </td>
                              <td>
                                {isAmountRow
                                  ? row.callCenterTotalValue
                                  : isNumberRow
                                  ? row.callCenterTotalValue
                                  : ""}
                              </td>
                              <td
                                className={
                                  isAmountRow || isNumberRow
                                    ? getDifferenceColorClass(
                                        isAmountRow
                                          ? parseFloat(row.differenceValue.replace("$", ""))
                                          : row.differenceValue
                                      )
                                    : ""
                                }
                                style={{
                                  backgroundColor: isAmountRow || isNumberRow ? "" : "inherit",
                                }}>
                                {isAmountRow
                                  ? row.differenceValue
                                  : isNumberRow
                                  ? row.differenceValue
                                  : ""}
                              </td>
                              <td>
                                {isAmountRow
                                  ? row.realityValue
                                  : isNumberRow
                                  ? row.realityValue
                                  : ""}
                              </td>
                              <td>
                                {isAmountRow ? row.stripeValue : isNumberRow ? row.stripeValue : ""}
                              </td>
                              <td>
                                {isAmountRow ? row.nmiValue : isNumberRow ? row.nmiValue : ""}
                              </td>
                              <td>
                                {isAmountRow
                                  ? row.realityTotalValue
                                  : isNumberRow
                                  ? row.realityTotalValue
                                  : ""}
                              </td>
                              <td>
                                {/* Display for all merchant rows */}
                                {!isNumberRow && !isTotal && isAmountRow && row.description && (
                                  <div className="d-flex flex-column align-items-center">
                                    <div className="mb-2">
                                      {(() => {
                                        // Parse values - remove $ sign and commas, then convert to float
                                        const crmTotal = parseFloat(
                                          row.crmTotalValue.replace("$", "").replace(/,/g, "")
                                        );
                                        const realityTotal = parseFloat(
                                          row.realityTotalValue.replace("$", "").replace(/,/g, "")
                                        );
                                        const difference = crmTotal - realityTotal;
                                        const formattedDiff = `$${difference.toLocaleString(
                                          "en-US",
                                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        )}`;

                                        // Return the difference with color based on value
                                        return (
                                          <span className={getDifferenceColorClass(difference)}>
                                            {formattedDiff}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                    {(row.description.toLowerCase().includes("auth") ||
                                      row.description.toLowerCase().includes("stripe") ||
                                      row.description.toLowerCase().includes("nmi")) && (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => {
                                          let transactionType = "auth";

                                          // Determine the transaction type based on the row description
                                          if (row.description.toLowerCase().includes("stripe")) {
                                            transactionType = "stripe";
                                          } else if (
                                            row.description.toLowerCase().includes("nmi")
                                          ) {
                                            transactionType = "nmi";
                                          } else if (
                                            row.description.toLowerCase().includes("auth")
                                          ) {
                                            transactionType = "auth";
                                          }

                                          openOrdersWithoutMatchesModal(
                                            transactionType,
                                            row.description
                                          );
                                        }}
                                        title={`View orders without transactions for ${row.description}`}>
                                        <Info size={14} /> See Details
                                      </Button>
                                    )}
                                  </div>
                                )}

                                {/* Display for number rows without button */}
                                {isNumberRow && (
                                  <div className="text-center">
                                    <span
                                      className={getDifferenceColorClass(
                                        row.crmTotalValue - row.realityTotalValue
                                      )}>
                                      {row.crmTotalValue - row.realityTotalValue}
                                    </span>
                                  </div>
                                )}

                                {/* Display for Total row */}
                                {isTotal && isAmountRow && (
                                  <div className="text-center font-weight-bold">
                                    {(() => {
                                      // Parse values - remove $ sign and commas, then convert to float
                                      const crmTotal = parseFloat(
                                        row.crmTotalValue.replace("$", "").replace(/,/g, "")
                                      );
                                      const realityTotal = parseFloat(
                                        row.realityTotalValue.replace("$", "").replace(/,/g, "")
                                      );
                                      const difference = crmTotal - realityTotal;
                                      const formattedDiff = `$${difference.toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`;

                                      // Return the difference with color based on value
                                      return (
                                        <span className={getDifferenceColorClass(difference)}>
                                          {formattedDiff}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="16">
                            <div
                              className="d-flex justify-content-center py-5"
                              style={{ height: "400px" }}>
                              {loading ? (
                                <div
                                  className="spinner-border text-primary align-self-center"
                                  role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              ) : (
                                <Empty description="No sales update data available." />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <Modal
              show={isExportModalVisible}
              onHide={() => setIsExportModalVisible(false)}
              centered>
              <Modal.Header closeButton>
                <Modal.Title>Export {exportType?.toUpperCase()}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Do you want to download the exported file or send it via email?</p>
                <div className="d-flex flex-column align-items-center gap-2">
                  <button className="btn btn-primary w-50" onClick={handleDownload}>
                    Download {exportType?.toUpperCase()}
                  </button>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter email address"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    style={{ width: "100%" }}
                  />
                  <button className="btn btn-secondary w-50" onClick={handleEmailSend}>
                    Share via Email
                  </button>
                </div>
              </Modal.Body>
            </Modal>

            {/* Modal for displaying orders without matches */}
            <OrdersWithoutMatchesModal
              visible={showOrdersWithoutMatchesModal}
              onClose={() => setShowOrdersWithoutMatchesModal(false)}
              filters={filters}
              timezone={timezone}
              transactionType={selectedTransactionType}
              merchantName={selectedMerchantName}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesUpdate;
